import { RoleName } from "@/api/users-types";
import { CreateRevision } from "@/components/prompts/CreateRevision";
import { DeletePrompt } from "@/components/prompts/DeletePrompt";
import { Revisions } from "@/components/prompts/Revisions";
import { UpdatePrompt } from "@/components/prompts/UpdatePrompt";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { PromptBreadcrumbs } from "@/components/ui/breadcrumbs/PromptBreadcrumbs";
import { RevisionBreadcrumbs } from "@/components/ui/breadcrumbs/RevisionBreadcrumbs";
import { TimestampBreadcrumbs } from "@/components/ui/breadcrumbs/TimestampBreadcrumbs";
import { BoxLoader } from "@/components/ui/loaders";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { TextBox, TextBoxRaw } from "@/components/ui/TextBox";
import { useHasRole } from "@/hooks/use-has-role";
import { usePromptById } from "@/queries/prompts/use-prompt-by-id";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";

export function PromptPage() {
  const { id, revisionNumber: revisionNumberParam } = useParams();
  const revisionNumber = revisionNumberParam
    ? parseInt(revisionNumberParam, 10)
    : undefined;

  const canView = useHasRole([
    RoleName.promptEngineer,
    RoleName.episodeManager,
  ]);

  return (
    <>
      {canView ? (
        <Stack gap={2} mb={2}>
          <PromptDetails id={id} />
          <Revision promptId={id} revisionNumber={revisionNumber} />
          <Revisions promptId={id} />
        </Stack>
      ) : (
        <NoPermissions />
      )}
    </>
  );
}

function PromptDetails({ id }: { id: string | undefined }) {
  const { data: prompt, isLoading, error } = usePromptById(id);

  return (
    <>
      <BoxLoader height={300} isLoading={isLoading} />
      <ApiErrorAlert error={error} />
      {id && prompt && (
        <>
          <Stack
            direction={"row"}
            gap={2}
            alignItems="center"
            justifyContent={"space-between"}
          >
            <PromptBreadcrumbs {...prompt} />
            {!prompt.deletedAt && (
              <Stack direction={"row"} gap={1}>
                <UpdatePrompt promptId={id} />
                <DeletePrompt id={id} />
              </Stack>
            )}
          </Stack>
          <TimestampBreadcrumbs {...prompt} />
          {prompt.deletedAt && (
            <Alert severity="error" sx={{ maxWidth: "1200px" }}>
              This prompt has been deleted
            </Alert>
          )}
          <TextBox>
            {prompt.description ? (
              <Typography color={"textSecondary"}>
                {`Description: ${prompt.description}`}
              </Typography>
            ) : (
              <Typography color={"textSecondary"}>No description</Typography>
            )}
          </TextBox>
        </>
      )}
    </>
  );
}

function Revision({
  promptId,
  revisionNumber,
}: {
  promptId: string | undefined;
  revisionNumber: number | undefined;
}) {
  const { data: prompt } = usePromptById(promptId);

  const navigate = useNavigate();

  if (!promptId || !prompt) {
    return null;
  }

  const header = (
    <Typography component={"h1"} variant="h6">
      Revision
    </Typography>
  );

  const revisions = prompt.revisions.toSorted(
    (r1, r2) => r1.revision - r2.revision,
  );
  const latestRevisionIdx = revisions.length - 1;

  // revision to display
  const currentRevisionIdx = revisionNumber
    ? revisions.findIndex((r) => r.revision === revisionNumber)
    : latestRevisionIdx;

  if (currentRevisionIdx === -1) {
    return (
      <>
        {header}
        <Alert
          sx={{ width: "1200px" }}
          severity="error"
        >{`Revision ${revisionNumber} not found`}</Alert>
      </>
    );
  }

  const currentRevision = revisions[currentRevisionIdx];
  const prevRevision =
    currentRevisionIdx > 0 ? revisions[currentRevisionIdx - 1] : undefined;
  const nextRevision =
    currentRevisionIdx < latestRevisionIdx
      ? revisions[currentRevisionIdx + 1]
      : undefined;

  const isLatestRevision = currentRevisionIdx === latestRevisionIdx;

  const navigation = (
    <Stack gap={1} direction={"row"}>
      <Button
        size="small"
        startIcon={<ArrowBack />}
        disabled={!prevRevision}
        onClick={() => {
          navigate(`/prompts/${promptId}/revisions/${prevRevision?.revision}`);
        }}
      >
        {prevRevision
          ? `Previous revision ( ${prevRevision.revision} )`
          : "No previous revision"}
      </Button>
      <Button
        size="small"
        endIcon={<ArrowForward />}
        disabled={!nextRevision}
        onClick={() => {
          navigate(`/prompts/${promptId}/revisions/${nextRevision?.revision}`);
        }}
      >
        {nextRevision
          ? `Next revision ( ${nextRevision.revision} )`
          : "No next revision"}
      </Button>
    </Stack>
  );

  return (
    <>
      <Stack
        direction={"row"}
        gap={2}
        alignItems="center"
        justifyContent={"space-between"}
      >
        {header}
        {!prompt.deletedAt && (
          <CreateRevision disabled={!isLatestRevision} promptId={promptId} />
        )}
      </Stack>
      <RevisionBreadcrumbs {...currentRevision} />
      <TimestampBreadcrumbs {...currentRevision} />
      <TextBox>
        {currentRevision.revisionNote ? (
          <Typography color={"textSecondary"}>
            {`Revision note: ${currentRevision.revisionNote}`}
          </Typography>
        ) : (
          <Typography color={"textSecondary"}>No revision note</Typography>
        )}
      </TextBox>
      {navigation}
      <TextBoxRaw>{currentRevision.prompt}</TextBoxRaw>
      {navigation}
    </>
  );
}
